import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostListener,
    OnInit,
    signal,
    inject,
    input,
    viewChild,
    output,
    DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatMenuModule } from '@angular/material/menu';
import { RouterModule } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { AppointmentRequestActivityType, DealerSearchDto } from '@warema/shared/models';
import { ProductsFilteredByCategoryPipe } from '@warema/shared/pipes';
import { WaremaButton, WaremaIcon } from '@warema/warema-ui-angular';
import { auditTime, Subject } from 'rxjs';

@Component({
    selector: 'warema-dealer-search-inputs-card',
    imports: [WaremaButton, WaremaIcon, TranslatePipe, RouterModule, MatMenuModule],
    providers: [ProductsFilteredByCategoryPipe],
    templateUrl: './dealer-search-inputs-card.component.html',
    styleUrls: ['./dealer-search-inputs-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DealerSearchInputsCardComponent implements OnInit, AfterViewInit {
    private readonly _productsFilteredByCategoryPipe = inject(ProductsFilteredByCategoryPipe);
    private readonly _destroyRef = inject(DestroyRef);

    private readonly _screenSizeChanged$ = new Subject<void>();
    private readonly _maxHeightOnSmallAndMedium = 420;

    public readonly dealerSearchDto = input.required<DealerSearchDto>();
    public readonly disableActionButtons = input(false);
    public readonly displayOnlyJumpToSearchAction = input(false);
    public readonly index = input(0);

    public readonly renameSearch = output<void>();
    public readonly openSearch = output<void>();
    public readonly deleteSearch = output<void>();
    public readonly jumpToDealerSearch = output<void>();

    public readonly card = viewChild.required<ElementRef>('card');

    public appointmentActivityType = AppointmentRequestActivityType;
    public externalProductSubcategories: Array<string> = [];
    public insideProductSubcategories: Array<string> = [];
    public patioProductSubcategories: Array<string> = [];
    public controlProductSubcategories: Array<string> = [];

    public readonly maxHeight = signal<string>(`${this._maxHeightOnSmallAndMedium}px`);
    public readonly isOverflowing = signal(false);
    public readonly isExpanded = signal(false);

    @HostListener('window:resize')
    public onResize(): void {
        this._screenSizeChanged$.next();
    }

    public ngAfterViewInit(): void {
        this._checkOverflow();
        this._screenSizeChanged$.pipe(auditTime(150), takeUntilDestroyed(this._destroyRef)).subscribe(() => {
            this._checkOverflow();
        });
    }

    public ngOnInit(): void {
        const dealerSearchDto = this.dealerSearchDto();
        this.externalProductSubcategories = this._productsFilteredByCategoryPipe.transform(
            dealerSearchDto.subProductCategories,
            '10',
        );
        this.insideProductSubcategories = this._productsFilteredByCategoryPipe.transform(
            dealerSearchDto.subProductCategories,
            '20',
        );
        this.patioProductSubcategories = this._productsFilteredByCategoryPipe.transform(
            dealerSearchDto.subProductCategories,
            '30',
        );
        this.controlProductSubcategories = this._productsFilteredByCategoryPipe.transform(
            dealerSearchDto.subProductCategories,
            '40',
        );
    }

    public expandCard(): void {
        this.maxHeight.set('unset');
        this.isOverflowing.set(false);
        this.isExpanded.set(true);
    }

    public shrinkCard(): void {
        this.maxHeight.set(`${this._maxHeightOnSmallAndMedium}px`);
        this.isOverflowing.set(true);
        this.isExpanded.set(false);
    }

    private _checkOverflow(): void {
        const productCategoriesEl = (this.card().nativeElement as HTMLElement).querySelector(
            '.product-categories',
        ) as HTMLElement;

        if (productCategoriesEl) {
            const scrollHeight = productCategoriesEl.scrollHeight;
            const clientHeight = productCategoriesEl.clientHeight;

            const isOverflowing = scrollHeight > clientHeight + 5; // kleiner Toleranzwert
            this.isOverflowing.set(isOverflowing);
        }
    }
}
